import React, { useState } from 'react';
import { FileText, Sparkles, Loader2, Wrench, Package, Info, Copy } from 'lucide-react';
import { carsysApi } from '../api/carsysApi';
import { useT } from '../context/LanguageContext';
import { toast } from 'sonner';

const eur = (n) => (Number(n) || 0).toLocaleString('nl-NL', { style: 'currency', currency: 'EUR', minimumFractionDigits: 2 });
const lab = { fontSize: 12, fontWeight: 600, color: 'var(--cs-text-muted)', display: 'block', margin: '0 0 4px' };
const inp = { width: '100%', padding: '9px 11px', border: '1px solid var(--cs-border)', borderRadius: 8, fontSize: 14, boxSizing: 'border-box' };

export default function OfferteAssistent() {
  const { t } = useT();
  const [d, setD] = useState({ klacht: '', kenteken: '', merk: '', model: '', bouwjaar: '', motor: '' });
  const [busy, setBusy] = useState(false);
  const [res, setRes] = useState(null);
  const upd = (k, v) => setD(p => ({ ...p, [k]: v }));

  const maak = async () => {
    if (!d.klacht.trim()) { toast.error(t('Beschrijf de klacht of het uit te voeren werk', 'Beschrijf de klacht of het uit te voeren werk')); return; }
    setBusy(true); setRes(null);
    try { setRes(await carsysApi.aiOfferte(d)); }
    catch (e) { toast.error(t('Mislukt', 'Mislukt'), { description: e.response?.data?.detail || e.message }); }
    finally { setBusy(false); }
  };

  const kopieer = () => {
    if (!res?.regels) return;
    const lines = res.regels.map(r => `${r.type}\t${r.omschrijving}\t${r.aantal}\t${eur(r.richtprijs)}\t${eur((Number(r.aantal) || 0) * (Number(r.richtprijs) || 0))}`).join('\n');
    navigator.clipboard.writeText(lines).then(() => toast.success(t('Gekopieerd naar klembord', 'Gekopieerd naar klembord'))).catch(() => {});
  };

  const totaal = res?.regels ? res.regels.reduce((s, r) => s + (Number(r.aantal) || 0) * (Number(r.richtprijs) || 0), 0) : 0;

  return (
    <div>
      <div className="cs-page-header">
        <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
          <div style={{ width: 40, height: 40, borderRadius: 10, background: '#2563eb', color: '#fff', display: 'flex', alignItems: 'center', justifyContent: 'center' }}><FileText size={20} /></div>
          <div><h2>{t('AI Offerte-assistent', 'AI Offerte-assistent')}</h2><p>{t('Zet een klacht om in concrete offerteregels (arbeid + onderdelen)', 'Zet een klacht om in concrete offerteregels (arbeid + onderdelen)')}</p></div>
        </div>
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: 'minmax(280px, 380px) 1fr', gap: 18, alignItems: 'start' }}>
        <div className="cs-card padded">
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10 }}>
            <div style={{ gridColumn: '1 / 3' }}><label style={lab}>{t('Klacht / uit te voeren werk', 'Klacht / uit te voeren werk')} *</label><textarea style={{ ...inp, minHeight: 90, resize: 'vertical' }} placeholder={t('bv. Trillingen bij remmen, piepend geluid voorzijde', 'bv. Trillingen bij remmen, piepend geluid voorzijde')} value={d.klacht} onChange={e => upd('klacht', e.target.value)} data-testid="of-klacht" /></div>
            <div><label style={lab}>{t('Kenteken', 'Kenteken')}</label><input style={inp} value={d.kenteken} onChange={e => upd('kenteken', e.target.value.toUpperCase())} data-testid="of-kenteken" /></div>
            <div><label style={lab}>{t('Merk', 'Merk')}</label><input style={inp} value={d.merk} onChange={e => upd('merk', e.target.value)} data-testid="of-merk" /></div>
            <div><label style={lab}>{t('Model', 'Model')}</label><input style={inp} value={d.model} onChange={e => upd('model', e.target.value)} data-testid="of-model" /></div>
            <div><label style={lab}>{t('Bouwjaar', 'Bouwjaar')}</label><input style={inp} value={d.bouwjaar} onChange={e => upd('bouwjaar', e.target.value)} data-testid="of-bouwjaar" /></div>
            <div style={{ gridColumn: '1 / 3' }}><label style={lab}>{t('Motor / inhoud', 'Motor / inhoud')}</label><input style={inp} value={d.motor} onChange={e => upd('motor', e.target.value)} data-testid="of-motor" /></div>
          </div>
          <button className="cs-btn primary" style={{ width: '100%', marginTop: 14, background: '#2563eb', borderColor: '#2563eb' }} onClick={maak} disabled={busy} data-testid="of-maak">
            {busy ? <Loader2 size={16} className="spin" /> : <Sparkles size={16} />} {t('Maak offerte-voorstel', 'Maak offerte-voorstel')}
          </button>
        </div>

        <div>
          {!res && !busy && (
            <div className="cs-card padded" style={{ textAlign: 'center', color: 'var(--cs-text-muted)', padding: 48 }}>
              <FileText size={40} style={{ opacity: 0.3 }} />
              <p style={{ marginTop: 12 }}>{t('Beschrijf de klacht en klik op "Maak offerte-voorstel" voor AI-offerteregels.', 'Beschrijf de klacht en klik op "Maak offerte-voorstel" voor AI-offerteregels.')}</p>
            </div>
          )}
          {busy && <div className="cs-card padded" style={{ textAlign: 'center', padding: 48 }}><Loader2 size={32} className="spin" style={{ color: '#2563eb' }} /><p style={{ marginTop: 10, color: 'var(--cs-text-muted)' }}>{t('AI stelt offerte op…', 'AI stelt offerte op…')}</p></div>}
          {res && Array.isArray(res.regels) && (
            <div data-testid="of-result">
              <div className="cs-card padded">
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 10 }}>
                  <div style={{ fontWeight: 700, color: '#2563eb', fontSize: 13 }}>{t('Voorgestelde offerteregels', 'Voorgestelde offerteregels')}</div>
                  <button className="cs-btn" onClick={kopieer} data-testid="of-kopieer" style={{ fontSize: 12 }}><Copy size={13} /> {t('Kopieer', 'Kopieer')}</button>
                </div>
                <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13.5 }}>
                  <thead>
                    <tr style={{ textAlign: 'left', color: 'var(--cs-text-muted)', fontSize: 12 }}>
                      <th style={{ padding: '6px 4px' }}>{t('Type', 'Type')}</th>
                      <th style={{ padding: '6px 4px' }}>{t('Omschrijving', 'Omschrijving')}</th>
                      <th style={{ padding: '6px 4px', textAlign: 'right' }}>{t('Aantal', 'Aantal')}</th>
                      <th style={{ padding: '6px 4px', textAlign: 'right' }}>{t('Prijs', 'Prijs')}</th>
                      <th style={{ padding: '6px 4px', textAlign: 'right' }}>{t('Totaal', 'Totaal')}</th>
                    </tr>
                  </thead>
                  <tbody>
                    {res.regels.map((r, i) => (
                      <tr key={i} style={{ borderTop: '1px solid var(--cs-border)' }}>
                        <td style={{ padding: '8px 4px' }}>
                          <span style={{ display: 'inline-flex', alignItems: 'center', gap: 4, fontSize: 12, fontWeight: 600, color: r.type === 'Arbeid' ? '#d97706' : '#16a34a' }}>
                            {r.type === 'Arbeid' ? <Wrench size={12} /> : <Package size={12} />} {r.type}
                          </span>
                        </td>
                        <td style={{ padding: '8px 4px' }}>{r.omschrijving}</td>
                        <td style={{ padding: '8px 4px', textAlign: 'right' }}>{r.aantal}</td>
                        <td style={{ padding: '8px 4px', textAlign: 'right' }}>{eur(r.richtprijs)}</td>
                        <td style={{ padding: '8px 4px', textAlign: 'right', fontWeight: 600 }}>{eur((Number(r.aantal) || 0) * (Number(r.richtprijs) || 0))}</td>
                      </tr>
                    ))}
                  </tbody>
                  <tfoot>
                    <tr style={{ borderTop: '2px solid var(--cs-border)' }}>
                      <td colSpan={4} style={{ padding: '10px 4px', textAlign: 'right', fontWeight: 700 }}>{t('Totaal indicatie (excl. BTW)', 'Totaal indicatie (excl. BTW)')}</td>
                      <td style={{ padding: '10px 4px', textAlign: 'right', fontSize: 17, fontWeight: 800, color: '#2563eb' }} data-testid="of-totaal">{eur(totaal)}</td>
                    </tr>
                  </tfoot>
                </table>
              </div>
              {res.toelichting && <p style={{ marginTop: 14, color: 'var(--cs-text)', lineHeight: 1.6 }}>{res.toelichting}</p>}
              {res.let_op && <div style={{ marginTop: 8, fontSize: 12, color: '#b45309', display: 'flex', gap: 6, alignItems: 'center' }}><Info size={14} /> {res.let_op}</div>}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
